import { useEffect, useState } from 'react';
import { _TypedDataEncoder } from 'ethers/lib/utils';
import { MultichainSwapResult } from '@shared/types/actions';
import { TradeMode } from '@shared/types/frontend/trade';
import { HiOutlineCog6Tooth } from 'react-icons/hi2';
import { useQuery } from '@tanstack/react-query';
import { TPnLData } from '@shared/types/frontend/assetPage';
import { getUrls } from '@shared/utils/config/environment';
import { ChainId } from '@shared/utils/config/networks';
import { toast } from 'sonner';
import { PriorityFeeLevels } from '@shared/types/services/enums';
import { numberFromString, toSpecialFixed } from '@/lib/misc/utils';
import { QUOTE_TIMEOUT_MS, useSwap } from '@/contexts/SwapProvider';
import NumberPad from '@/components/trade/NumberPad';
import SelectionScreen from '../mobile/SelectionScreen';
import { validate } from './const';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { useSession } from '@/contexts/LitSessionProvider';
import SwapSuccess from '../mobile/SwapSuccess';
import SwipeConfirm from '../mobile/SwipeConfirm';
import { SOLANA_NATIVE_ADDRESS, SOLANA_NATIVE_SIMPLE } from '@/lib/asset/nativeAddressHelper';
import classNames from '@/lib/misc/classNames';
interface Props {
  onClose: () => void;
  tradeMode: TradeMode;
  setSettingsModalOpen: (value: boolean) => void;
}
export default function SwapTabMobile(p: Props) {
  const {
    fromAssets,
    setFromAssets,
    toAssets,
    swapResult,
    setSwapResult,
    triggerQuote,
    swapWrapper,
    quoteData,
    swapLoading,
    setSwapLoading,
    lastQuoteTime,
    waitForQuoteData,
    fromConfig
  } = useSwap();
  const [isSwitched, setIsSwitched] = useState(false);
  const [showAmount, setShowAmount] = useState('0');
  const [tokenAmountToSend, setTokenAmountToSend] = useState('0');
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    PKP
  } = useSession();
  const isQuoteError = (quoteData as any)?.statusError || (quoteData as any)?.message && parseFloat(showAmount) > 0;
  const disableInteraction = p.tradeMode !== TradeMode.SWAP && !fromAssets[0] || swapLoading;
  const error = Boolean(!validate(fromAssets, toAssets).valid && validate(fromAssets, toAssets).error && validate(fromAssets, toAssets)?.error !== 'Swap');
  const {
    data: pnLData
  } = useQuery({
    queryKey: ['pnlData', ...(fromAssets && fromAssets[0] ? [fromAssets[0]?.contract_address, fromAssets[0]?.network] : [])],
    queryFn: async (): Promise<TPnLData | undefined> => {
      if (fromAssets && fromAssets[0]) {
        const data = await fetch(`${getUrls().INDEXER_URL}/portfolio/holdings?address=${fromAssets[0].network == ChainId.SOLANA ? PKP?.solanaAddress : PKP?.additionalEthAddress}&token=${fromAssets[0]?.contract_address}&chains=${fromAssets[0]?.network}`).then(res => res.json());
        const pnls = data?.pnls?.[0] ?? null;
        return pnls;
      }
    },
    enabled: p.tradeMode === TradeMode.SELL && Boolean(fromAssets[0] && fromAssets && fromAssets[0]?.contract_address && fromAssets[0]?.network && PKP)
  });
  useEffect(() => {
    if (!fromAssets[0]) return;
    const amount = fromAssets[0]?.amountToUseStr ?? '0';
    if (fromAssets[0]?.quote_rate) {
      const rate = Number(fromAssets[0].quote_rate);
      const _tokenAmountToSend = (Math.round(Number(amount) / rate * 100) / 100).toString();
      setTokenAmountToSend(_tokenAmountToSend);
      setShowAmount(isSwitched ? _tokenAmountToSend : amount);
    } else if (!isSwitched) {
      setShowAmount(amount);
    }
  }, [isSwitched]);

  // Resets the switch state and values when the from asset changes
  useEffect(() => {
    setIsSwitched(false);
    setShowAmount('0');
    setTokenAmountToSend('0');
  }, [fromAssets?.[0]?.contract_address]);
  const handleNumPadChange = (val: string, isSwitched: boolean) => {
    if (!fromAssets[0]) return;
    if (!val) {
      val = '0';
    }
    if (val === '') {
      val = '0';
    }
    if (val?.includes('.')) {
      const parts = val.split('.');
      const integerPart = parts[0];
      let decimalPart = parts[1] || '';
      const decimalLength = decimalPart.length;
      if (decimalLength > 3) {
        decimalPart = decimalPart.substring(0, 3);
      }
      val = `${integerPart}.${decimalPart}`;
    }
    const parsedVal = parseFloat(val.replace(/,/g, ''));
    if (isNaN(parsedVal)) {
      console.error('Invalid numeric input:', val);
      return;
    }
    const rate = Number(fromAssets[0]?.quote_rate);
    if (isNaN(rate)) {
      console.error('Invalid quote rate:', fromAssets[0]?.quote_rate);
      return;
    }
    setShowAmount(val);
    isSwitched && setTokenAmountToSend(val);
    setFromAssets([{
      ...fromAssets[0],
      amountToUseStr: isSwitched ? toSpecialFixed(parsedVal * rate).value : val,
      amountToUse: numberFromString(isSwitched ? toSpecialFixed(parsedVal * rate).value : val)
    }]);
  };
  const setPercentage = (percentage: number) => {
    if (!fromAssets[0]) return;
    const _amount = fromAssets[0]?.usd_value * (percentage / 100);
    const formattedUsdAmount = toSpecialFixed(_amount, 2).value;
    if (fromAssets[0]?.quote_rate) {
      const rate = Number(fromAssets[0].quote_rate);
      const tokenAmount = toSpecialFixed(Number(formattedUsdAmount) / rate, 3).value;
      setTokenAmountToSend(tokenAmount);
      setShowAmount(isSwitched ? tokenAmount : formattedUsdAmount);
    } else {
      setShowAmount(formattedUsdAmount);
    }
    setFromAssets([{
      ...fromAssets[0],
      amountToUseStr: formattedUsdAmount,
      amountToUse: Number(formattedUsdAmount)
    }]);
  };
  const swapResultTokenIn = (swapResult as MultichainSwapResult)?.decodedResponse?.quote?.quote?.tokenIn;
  const swapResultTokenOut = (swapResult as MultichainSwapResult)?.decodedResponse?.quote?.quote?.tokenOut;
  const compareSuccessAddresses = (a: string, b: string) => {
    return a.toLowerCase() === b.toLowerCase() || a.toLowerCase() === SOLANA_NATIVE_ADDRESS.toLowerCase() && b.toLowerCase() === SOLANA_NATIVE_SIMPLE.toLowerCase();
  };
  const showSuccess = (swapResult as MultichainSwapResult)?.time && compareSuccessAddresses(fromAssets[0]?.contract_address, swapResultTokenIn?.address) && compareSuccessAddresses(toAssets[0]?.contract_address, swapResultTokenOut?.address);
  const showCustomSlippage = fromConfig.slippage !== 'auto';
  const showCustomGas = !Object.values(PriorityFeeLevels).includes(fromConfig.gas);
  return (swapResult as MultichainSwapResult)?.time && showSuccess ? p.tradeMode === TradeMode.SELL ? <SwapSuccess tradeMode={p.tradeMode} asset={fromAssets[0]} pnLData={pnLData} closeModal={p.onClose} showConfetti /> : p.tradeMode === TradeMode.BUY ? <SwapSuccess tradeMode={p.tradeMode} asset={toAssets[0]} amount={fromAssets[0].amountToUse.toFixed(2)} orderId={(swapResult as MultichainSwapResult).order_id} closeModal={p.onClose} showConfetti /> : <SwapSuccess tradeMode={p.tradeMode} asset={toAssets[0]} fromAsset={fromAssets[0]} orderId={(swapResult as MultichainSwapResult).order_id} closeModal={p.onClose} showConfetti /> : <div className='relative w-full flex flex-col gap-4 pb-6' data-sentry-component="SwapTabMobile" data-sentry-source-file="SwapTabMobile.tsx">
      <div onClick={() => p.setSettingsModalOpen(true)} className={classNames(`absolute top-[-16px] left-4 flex items-center gap-2 border-genius-blue border-[1px] rounded-full text-xs leading-[100%] text-genius-cream p-2`, {
      'pr-3': showCustomSlippage || showCustomGas
    })}>
        <HiOutlineCog6Tooth size={16} data-sentry-element="HiOutlineCog6Tooth" data-sentry-source-file="SwapTabMobile.tsx" />
        {showCustomSlippage ? <span>{fromConfig.slippage}%</span> : null}
        {showCustomSlippage && showCustomGas ? <span className='opacity-50'>|</span> : null}
        {showCustomGas ? <span>{fromConfig.gas} SOL</span> : null}
      </div>

      <div className='flex flex-col items-center'>
        <div className='text-[16px] font-medium translate-y-[-12px]'>{p.tradeMode}</div>
        <SelectionScreen tradeMode={p.tradeMode} showAmount={showAmount} setShowAmount={setShowAmount} isSwitched={isSwitched} setIsSwitched={setIsSwitched} setPercentage={setPercentage} disableInteraction={disableInteraction} data-sentry-element="SelectionScreen" data-sentry-source-file="SwapTabMobile.tsx" />
      </div>

      <div className='flex flex-col gap-4 px-4'>
        <NumberPad value={fromAssets[0]?.amountToUseStr ?? '0'} onValueChange={handleNumPadChange} tokenAmountToSend={tokenAmountToSend} isSwitched={isSwitched} disabled={disableInteraction || p.tradeMode === TradeMode.SWAP && !fromAssets[0]} data-sentry-element="NumberPad" data-sentry-source-file="SwapTabMobile.tsx" />

        <SwipeConfirm swapLoading={swapLoading} disabled={!validate(fromAssets, toAssets).valid || swapLoading} text={error ? validate(fromAssets, toAssets).error : swapLoading ? 'Loading...' : swapResult?.error || isQuoteError ? 'Error, try again' : `Swipe to ${p.tradeMode}`} hasError={error || swapResult?.error || isQuoteError} onConfirm={async () => {
        LogToMixpanel({
          pkp: PKP,
          eventName: EVENTS.trade.startTrade,
          category: CATEGORIES.trade,
          metaData: {
            fromAssets: fromAssets,
            toAssets: toAssets
          }
        });
        setSwapResult({
          error: '',
          time: 0
        });
        setSwapLoading(true);

        // Waits for quote in case of already ongoing quote trigger (e.g. after keypad input)
        const startTime = Date.now();
        let quoteData = await waitForQuoteData();
        if (!quoteData?.response?.[0]) {
          const secondsElapsed = (Date.now() - startTime) / 1000;
          if (secondsElapsed > 3.5) {
            toast.info('This is taking a little longer than usual, hang tight!', {
              duration: 1500
            });
          }
          await triggerQuote();
          quoteData = await waitForQuoteData();
        }
        if (!quoteData?.response?.[0]) {
          // TODO: add better error logging from quoting engine
          toast.error('There was an issue executing your order, please increase your slippage and try again.');
          setShowAmount('0');
          setFromAssets(prev => [{
            ...prev[0],
            amountToUseStr: '0',
            amountToUse: 0
          }]);
          setSwapLoading(false);
          return;
        }
        if (!quoteData || lastQuoteTime.current === null || Date.now() - lastQuoteTime.current > QUOTE_TIMEOUT_MS) {
          // In the case of quoteData is null or stale, trigger a new quote before swapping
          await triggerQuote();
          quoteData = await waitForQuoteData();
          await swapWrapper(quoteData);
        } else {
          // Otherwise, use the fresh quoteData
          await swapWrapper(quoteData);
        }
      }} data-sentry-element="SwipeConfirm" data-sentry-source-file="SwapTabMobile.tsx" />
      </div>
    </div>;
}